import React, { useState } from 'react';
import { getTravelApiBaseUrl } from '../lib/apiConfig';

// Mirrors CONSENT_TEXT in backend/travel/p3Sms.js (stored with each sign-up as proof of consent).
const CONSENT_TEXT = 'Yes, text me trip updates from Freshroad Travel Pass, including results of calls Milo makes for me, booking changes and trip reminders. Message frequency varies. Msg & data rates may apply. Reply HELP for help, STOP to cancel. Consent is not a condition of purchase.';
const SUPPORT_EMAIL = 'info@freshroad.com';

const css = `
.sms-page{ min-height:100vh; background:#f6f3ec; color:#15263d; font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif; padding:32px 16px; box-sizing:border-box; }
.sms-card{ max-width:520px; margin:0 auto; background:#fff; border:1px solid #e6e1d6; border-radius:20px; padding:28px; box-shadow:0 10px 30px rgba(21,38,61,.06); }
.sms-brand{ font-size:12px; font-weight:800; letter-spacing:.12em; text-transform:uppercase; color:#b98a3e; }
.sms-page h1{ font-size:26px; margin:6px 0 8px; color:#15263d; text-transform:none; }
.sms-page h2{ font-size:16px; margin:24px 0 6px; color:#15263d; text-transform:none; }
.sms-page p, .sms-page li{ font-size:15px; line-height:1.55; color:#3c4a5c; }
.sms-page label{ display:block; font-weight:700; font-size:14px; margin:18px 0 6px; }
.sms-page input[type=tel]{ width:100%; box-sizing:border-box; font:inherit; font-size:17px; padding:12px 14px; border:1px solid #cfd6e0; border-radius:12px; }
.sms-page input[type=tel]:focus{ outline:2px solid #b98a3e; outline-offset:1px; }
.sms-consent{ display:flex !important; gap:10px; align-items:flex-start; font-weight:400 !important; font-size:14px !important; line-height:1.5; color:#3c4a5c; cursor:pointer; }
.sms-consent input{ width:20px; height:20px; margin-top:2px; flex:0 0 20px; }
.sms-btn{ margin-top:20px; width:100%; min-height:48px; border:0; border-radius:12px; background:#15263d; color:#fff; font:inherit; font-size:16px; font-weight:700; cursor:pointer; text-transform:none; }
.sms-btn:disabled{ opacity:.5; cursor:not-allowed; }
.sms-err{ margin-top:12px; padding:10px 12px; border-radius:10px; background:#fdecec; color:#9b1c1c; font-size:14px; }
.sms-ok{ font-size:40px; }
.sms-terms{ max-width:520px; margin:18px auto 0; padding:0 4px; }
.sms-terms p, .sms-terms li{ font-size:13px; color:#5b6878; }
.sms-page a{ color:#15263d; }
`;

/** Public sign-up form for Freshroad Travel Pass text updates (A2P campaign opt-in). */
export default function SmsOptIn() {
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${getTravelApiBaseUrl()}/api/p3/sms-optin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, consent, pageUrl: window.location.href }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) throw new Error(data?.message || 'Something went wrong. Please try again.');
      setDone(phone.trim());
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sms-page">
      <style>{css}</style>
      <main className="sms-card">
        <div className="sms-brand">Freshroad Travel Pass</div>
        {done ? (
          <>
            <div className="sms-ok" aria-hidden="true">✅</div>
            <h1>You're signed up</h1>
            <p>We'll text a confirmation to <strong dir="ltr">{done}</strong>. After that, Milo will text you updates about your trip and the calls it makes for you.</p>
            <p>Message frequency varies. Msg &amp; data rates may apply. Reply <strong>HELP</strong> for help or <strong>STOP</strong> to cancel at any time.</p>
          </>
        ) : (
          <form onSubmit={submit} noValidate>
            <h1>Text updates from Milo</h1>
            <p>Milo, your Freshroad Travel Pass assistant, can text you when it finishes a call for you (for example, “Checkout moved to 2:00 PM”), when a booking changes, and before your plans start.</p>
            <label htmlFor="sms-phone">Mobile number</label>
            <input id="sms-phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="+1 415 555 0123" value={phone} onChange={(e) => setPhone(e.target.value)} required dir="ltr" />
            <label className="sms-consent">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} required />
              <span>{CONSENT_TEXT} See our <a href="#/site/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and <a href="#/site/terms" target="_blank" rel="noopener noreferrer">Terms</a>.</span>
            </label>
            <button className="sms-btn" type="submit" disabled={busy || !phone.trim() || !consent}>{busy ? 'Signing up…' : 'Sign up for text updates'}</button>
            {error && <div className="sms-err" role="alert">{error}</div>}
          </form>
        )}
      </main>

      <section className="sms-terms" aria-label="SMS terms">
        <h2>SMS terms</h2>
        <ul>
          <li><strong>Program:</strong> Freshroad Travel Pass trip updates, operated by Fresh Road Inc.</li>
          <li><strong>What you'll get:</strong> results of calls Milo makes for you, booking and reservation changes, and reminders about your trip. No marketing messages.</li>
          <li><strong>Frequency:</strong> varies with your trip. Message and data rates may apply.</li>
          <li><strong>Opt out:</strong> reply STOP at any time. You'll get one message confirming you're unsubscribed.</li>
          <li><strong>Help:</strong> reply HELP or email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.</li>
          <li><strong>Privacy:</strong> we do not sell or share your mobile number or SMS consent with third parties for their marketing purposes. See the <a href="#/site/privacy">Privacy Policy</a>.</li>
          <li>Carriers are not liable for delayed or undelivered messages.</li>
        </ul>
      </section>
    </div>
  );
}
